'use client'

import { Badge, Card, CardContent, CardHeader, CardTitle, ClipboardList } from '@chain-zen/ui'
import { useMemo, useState } from 'react'

import type { IShipment, IShipmentFile } from '../../../../../types/shipment'
import { downloadFileFromURL, normalizeFileURL } from '../../../../../utils/download-file'
import { getExtractedDataEntries, normalizeShipmentFiles } from './files-details.utils'
import { ShipmentFileListItem } from './shipment-file-list-item'
import { ShipmentFilePreviewDialog } from './shipment-file-preview-dialog'

/**
 * Files details component renders the details of a shipment's files
 * It displays the files of the shipment
 */
export const FilesDetails = ({ shipment }: { shipment: IShipment }) => {
  const [selectedFile, setSelectedFile] = useState<IShipmentFile | null>(null)
  const files = useMemo(() => normalizeShipmentFiles(shipment?.files), [shipment?.files])
  const selectedFileEntries = useMemo(() => {
    if (!selectedFile) return []

    return getExtractedDataEntries(selectedFile)
  }, [selectedFile])

  const handleDownload = async (url: string) => {
    const didDownload = await downloadFileFromURL({
      fallbackToDirectLink: false,
      fetchOptions: { mode: 'cors' },
      url
    })

    if (!didDownload) {
      window.open(normalizeFileURL(url), '_blank', 'noopener,noreferrer')
    }
  }

  if (!shipment || files.length === 0) return null

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ClipboardList className="h-4 w-4 text-primary" />
            Files
            <Badge variant="outline" className="ml-auto">
              {files.length}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {files.map((file, index) => (
            <ShipmentFileListItem
              key={`${file.url}-${file.classificationType}-${file.timestamp || index}`}
              file={file}
              onDownload={handleDownload}
              onPreview={setSelectedFile}
            />
          ))}
        </CardContent>
      </Card>
      <ShipmentFilePreviewDialog
        entries={selectedFileEntries}
        onClose={() => setSelectedFile(null)}
        onDownload={handleDownload}
        selectedFile={selectedFile}
      />
    </>
  )
}
